import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, CheckCircle, AlertCircle, FileText, Mail, Phone, ExternalLink, Clock, Users } from 'lucide-react';
import { GrasrotandelenBanner } from '../components/GrasrotandelenBanner';

import i18n from '../i18n';
import { useTranslation } from 'react-i18next';
const t = (k, o) => i18n.t(k, o);

export const Politiattest = () => {
  useTranslation();
  return (
    <div data-testid="politiattest-page" className="min-h-screen bg-slate-50 dark:bg-slate-900">
      {/* Hero Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-white dark:bg-slate-800">
        <div className="max-w-4xl mx-auto">
          <Link 
            to="/styret" 
            className="inline-flex items-center gap-2 text-slate-500 hover:text-green-600 transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('pg.Politiattest.6e689e')}
          </Link>
          
          <div className="flex items-center gap-4 mb-6">
            <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center">
              <Shield className="w-7 h-7 text-green-600" />
            </div>
            <div>
              <p className="text-green-600 font-medium uppercase tracking-widest mb-1">{t('pg.Politiattest.0bbd3b')}</p>
              <h1 className="text-4xl sm:text-5xl font-bold tracking-tighter text-slate-900 dark:text-white">
                {t('pg.Politiattest.d07601')}
              </h1>
            </div>
          </div>
          
          <p className="text-slate-600 text-lg leading-relaxed">
            {t('pg.Politiattest.007951')}
          </p>
        </div>
      </section>

      {/* Who needs it */}
      <section className="px-4 sm:px-6 lg:px-8 pb-12">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-green-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <Users className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-900 mb-3">{t('pg.Politiattest.68876d')}</h2>
                <p className="text-slate-600 dark:text-slate-300">
                  {t('pg.Politiattest.fc4b07')}
                </p>
                <ul className="mt-4 space-y-2">
                  {[t('pg.Politiattest.9f0ca7'), t('pg.Politiattest.597d08'), t('pg.Politiattest.a1f0db'), t('pg.Politiattest.84df5d')].map((role) => (
                    <li key={role} className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                      {role}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Important to know */}
      <section className="px-4 sm:px-6 lg:px-8 pb-12">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
            <AlertCircle className="w-6 h-6 text-green-500" />
            {t('pg.Politiattest.2864f9')}
          </h2>
          
          <div className="space-y-4">
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-red-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <AlertCircle className="w-4 h-4 text-red-400" />
                </div>
                <div>
                  <p className="text-slate-900 font-medium mb-1">{t('pg.Politiattest.d9e35b')}</p>
                  <p className="text-slate-500 text-sm">
                    {t('pg.Politiattest.fa0d26')}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-green-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                </div>
                <div>
                  <p className="text-slate-900 font-medium mb-1">{t('pg.Politiattest.ab5381')}</p>
                  <p className="text-slate-500 text-sm">
                    {t('pg.Politiattest.de9d19')}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-blue-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Clock className="w-4 h-4 text-blue-400" />
                </div>
                <div>
                  <p className="text-slate-900 font-medium mb-1">{t('pg.Politiattest.c6f0c2')}</p>
                  <p className="text-slate-500 text-sm">
                    {t('pg.Politiattest.c8653d')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How to proceed */}
      <section className="px-4 sm:px-6 lg:px-8 pb-12">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
            <FileText className="w-6 h-6 text-green-500" />
            {t('pg.Politiattest.b4fcbe')}
          </h2>
          
          <div className="space-y-4">
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-black font-bold">1</span>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">{t('pg.Politiattest.9caf34')}</h3>
                  <p className="text-slate-500 dark:text-slate-400">
                    {t('pg.Politiattest.3a575f')}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-black font-bold">2</span>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">{t('pg.Politiattest.1e4d34')}</h3>
                  <p className="text-slate-500 dark:text-slate-400">
                    {t('pg.Politiattest.6f36b2')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="px-4 sm:px-6 lg:px-8 pb-12">
        <div className="max-w-4xl mx-auto">
          <div className="bg-green-500/10 border border-green-500/30 rounded-2xl p-6 sm:p-8">
            <h2 className="text-xl font-bold text-slate-900 mb-4">
              {t('pg.Politiattest.7a3409')}
            </h2>
            
            <div className="space-y-4">
              <a 
                href="mailto:nickolas.paynter@veitvet-sportsklubb.no"
                className="flex items-center gap-3 text-green-500 hover:underline"
              >
                <Mail className="w-5 h-5" />
                nickolas.paynter@veitvet-sportsklubb.no
              </a>
              
              <div className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-green-500 mt-0.5" />
                <div>
                  <a href="tel:+4790739749" className="text-green-500 hover:underline">
                    +47 907 39 749
                  </a>
                  <p className="text-slate-500 text-sm mt-1">
                    {t('pg.Politiattest.1e5b75')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* More information */}
      <section className="px-4 sm:px-6 lg:px-8 pb-12">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
            <h2 className="text-xl font-bold text-slate-900 mb-4">{t('pg.Politiattest.bb0445')}</h2>
            <p className="text-slate-600 mb-6">
              {t('pg.Politiattest.53d742')}
            </p>
            <a 
              href="https://www.idrettsforbundet.no/digital/politiattest/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-green-500 text-black font-bold px-6 py-3 rounded-lg hover:bg-green-500/90 transition-colors"
            >
              {t('pg.Politiattest.37536c')}
              <ExternalLink className="w-4 h-4" />
            </a>
            <p className="text-slate-400 text-sm mt-3">
              {t('pg.Politiattest.14bc3d')}
            </p>
          </div>
        </div>
      </section>

      {/* Thank you message */}
      <section className="px-4 sm:px-6 lg:px-8 pb-20">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-r from-green-500/20 to-transparent rounded-2xl p-6 sm:p-8 border border-green-500/30">
            <p className="text-lg sm:text-xl text-slate-900 font-medium">
              {t('pg.Politiattest.3d32b1')}
            </p>
          </div>
        </div>
      </section>

      {/* Grasrotandelen Banner */}
      <GrasrotandelenBanner />
    </div>
  );
};

export default Politiattest;
